// Launch Darkly also supports JSON flag values. This implementation doesn't
// support this yet, but we can add it later if we need/want it.
export type LaunchDarklyFlagValue = boolean | number | string

export interface WebFeatureFlags {
  'client-side-report-pdf-export': boolean
  'configure-departments-permission-feature': boolean
  'departments-for-realtime': boolean
  'design-system-overlay': boolean
  'detailed-error-messages': boolean
  'disable-chromium-remapping-hack': boolean
  'feature-annotations-departments-display': boolean
  'feature-annotations-manage-people-settings': boolean
  'feature-annotations-people-case-page': boolean
  'feature-automatic-annotations': string
  'feature-disable-channel-remapping': boolean
  'feature-stt-editing': boolean
  'filter-recordings-list-by-on-the-record-content': boolean
  'licensing-add-ons': boolean
  'pac-standard-rules': boolean
  'real-time-stt-attribution-approach':
    | 'never-use-software-based-attribution'
    | 'court-system-configures-attribution-per-room'
    | 'always-use-software-based-attribution'
  'realtime-recording-automated-deletion': boolean
  'realtime-recording-on-demand-deletion': boolean
  'recording-vault-activity-log-liveness': boolean
  'recording-vault-support': boolean
  'release-pac-configuration': boolean
  'release-partial-export': boolean
  'reporting-share': boolean
  'reporting-subscriptions': boolean
  'revoke-refresh-token': boolean
  'rth-configure-video-devices': boolean
  'rth-courtroom-display': boolean
  'rth-defined-channel-audio-emission': number // 0 = hybrid recorder emission disabled
  'rth-display-debug': boolean
  'rth-enabled-realtime-hearings': boolean
  'rth-feature-show-infrastructure-status': boolean
  'rth-hybrid-audio-routing': boolean
  'rth-join-courtroom-remotely': boolean
  'rth-manage-courtroom': boolean
  'rth-schedule-proceedings': boolean
  'standard-stt-delay-seconds': number
  'stt-debug-ui-tooling': boolean
  // leave this one for unit tests
  'test-boolean-flag': boolean
  'trm-recording-channel-map-strategy': number // See LD for what values map back to
  'trm-reports': boolean
  'upload-and-play': boolean
  'users-reports': boolean
  'department-audit-logs': boolean
  'stripe-version-upgrade': boolean
  'courtroom-monitoring-overview-actions-menu': boolean
  'departs-for-realtime-edit-live-or-future-sessions': boolean
  'use-joda-types-for-stt-endpoints': boolean
}

export type FeatureFlagKeyOfType<TValue> = Exclude<
  keyof {
    [K in keyof WebFeatureFlags as WebFeatureFlags[K] extends TValue ? K : never]: never
  },
  symbol | number
>

/* Defined here to avoid circular dependency with real-time-stt.component.ts */
export const DEFAULT_STANDARD_STT_DELAY_SECONDS = 240

export const defaultFlags: WebFeatureFlags = {
  'client-side-report-pdf-export': false,
  'configure-departments-permission-feature': false,
  'departments-for-realtime': false,
  'design-system-overlay': false,
  'detailed-error-messages': false,
  'disable-chromium-remapping-hack': false,
  'feature-annotations-departments-display': false,
  'feature-annotations-manage-people-settings': false,
  'feature-annotations-people-case-page': false,
  'feature-automatic-annotations': 'off',
  'feature-disable-channel-remapping': false,
  'feature-stt-editing': false,
  'filter-recordings-list-by-on-the-record-content': false,
  'licensing-add-ons': false,
  'pac-standard-rules': false,
  'real-time-stt-attribution-approach': 'never-use-software-based-attribution',
  'realtime-recording-automated-deletion': false,
  'realtime-recording-on-demand-deletion': false,
  'recording-vault-activity-log-liveness': false,
  'recording-vault-support': false,
  'release-pac-configuration': false,
  'release-partial-export': false,
  'reporting-share': false,
  'reporting-subscriptions': false,
  'revoke-refresh-token': false,
  'rth-configure-video-devices': false,
  'rth-courtroom-display': false,
  'rth-defined-channel-audio-emission': 0, // 0 = hybrid recorder emission disabled
  'rth-display-debug': false,
  'rth-enabled-realtime-hearings': false,
  'rth-feature-show-infrastructure-status': false,
  'rth-hybrid-audio-routing': false,
  'rth-join-courtroom-remotely': false,
  'rth-manage-courtroom': false,
  'rth-schedule-proceedings': false,
  'standard-stt-delay-seconds': DEFAULT_STANDARD_STT_DELAY_SECONDS,
  'stt-debug-ui-tooling': false,
  'test-boolean-flag': false,
  'trm-recording-channel-map-strategy': 0,
  'trm-reports': false,
  'upload-and-play': true,
  'users-reports': false,
  'department-audit-logs': false,
  'stripe-version-upgrade': false,
  'courtroom-monitoring-overview-actions-menu': false,
  'departs-for-realtime-edit-live-or-future-sessions': false,
  'use-joda-types-for-stt-endpoints': false,
}
